<template>
  <div class="container mt-4">
    <button @click="goBack" class="btn btn-secondary mb-3">
      <font-awesome-icon icon="arrow-left" /> Zurück zur Übersicht
    </button>

    <h2 class="text-center mb-4">Artikel Übersicht</h2>
    <!-- Price-Toggle -->
    <div class="form-check form-switch mb-2">
      <input
        class="form-check-input"
        type="checkbox"
        id="togglePrice"
        v-model="includePrice"
      />
      <label class="form-check-label" for="togglePrice">
        Preise im PDF anzeigen
      </label>
    </div>
    <button @click="generatePdf" class="btn btn-primary mb-3">PDF erstellen</button>
    <GeneratePdf
      ref="pdfGenerator"
      :customerName="selectedCustomerName"
      :customerId="selectedCustomerId"
      :articles="filteredArticles"
      :includePrice="includePrice"
    />

    <!-- Suchfeld -->
    <div class="row">
      <div class="col-12 col-md-6 mx-auto">
        <input
          type="text"
          v-model="searchQuery"
          class="form-control mb-3"
          placeholder="Suche Artikel oder Kunden..."
        />
      </div>
    </div>

    <!-- Status-Filter Buttons -->
    <div class="d-flex justify-content-center mb-2">
      <button
        v-for="(visible, status) in showStatus"
        :key="status"
        @click="toggleStatus(status)"
        :class="btnClass(status)"
        class="mx-1"
      >
        {{ labels[status] }}
      </button>
    </div>

    <!-- Neuer Filter: Nur retour Markierte -->
    <div class="d-flex justify-content-center mb-4">
      <div class="form-check form-switch">
        <input
          class="form-check-input"
          type="checkbox"
          id="onlyMarkedForReturn"
          v-model="onlyMarkedForReturn"
        />
        <label class="form-check-label" for="onlyMarkedForReturn">
          Nur Rückgabe-Artikel anzeigen
        </label>
      </div>
    </div>

    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Laden...</span>
      </div>
    </div>
    <!-- Tabelle -->
    <div v-else class="table-responsive">
      <table class="table table-striped table-hover table-bordered">
        <thead class="table-light">
          <tr>
            <th @click="sortBy('ArticleId')" style="width: 8%">ID</th>
            <th @click="sortBy('Name')" style="width: 15%">Bezeichnung</th>
            <th @click="sortBy('Color')" style="width: 10%">Farbe</th>
            <th @click="sortBy('Size')" style="width: 5%">Größe</th>
            <th @click="sortBy('Gender')" style="width: 5%">Gender</th>
            <th @click="sortBy('ReceivedDate')" style="width: 12%">Erhalten</th>
            <th @click="sortBy('SaleDate')" style="width: 12%">Verkauf</th>
            <th @click="sortBy('discountedPrice')" style="width: 12%">Akt. Preis</th>
            <th style="width: 20%">Kunde</th>
            <th style="width: 8%">Status</th>
            <th style="width: 15%">Aktionen</th>
          </tr>
        </thead>
        <tbody>
          <ArticleRow
            v-for="article in filteredArticles"
            :key="article.ArticleId"
            :article="article"
            @edit="openEditModal"
            @delete="openDeleteModal"
          />
        </tbody>
      </table>
    </div>

    <!-- Edit-Modal -->
    <EditArticleModal
      v-if="selectedArticle"
      :article="selectedArticle"
      @save="saveArticle"
      @close="closeModal"
    />

    <!-- Delete-Modal -->
    <div v-if="articleToDelete">
      <div class="modal-backdrop fade show"></div>
      <div class="modal fade show" style="display: block;" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Artikel löschen</h5>
              <button type="button" class="btn-close" @click="closeDeleteModal" aria-label="Schliessen"></button>
            </div>
            <div class="modal-body">
              <p>Möchten Sie den Artikel {{ articleToDelete.ArticleId }} ({{ articleToDelete.Name }}) wirklich löschen?</p>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeDeleteModal">Abbrechen</button>
              <button type="button" class="btn btn-danger" @click="deleteArticle">Löschen</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/services/axios';
import EditArticleModal from './EditArticleModal.vue';
import GeneratePdf from './PdfGenerator.vue';
import ArticleRow from './ArticleRow.vue';

export default {
  components: { EditArticleModal, GeneratePdf, ArticleRow },
  data() {
    return {
      articles: [],
      loading: false,
      searchQuery: '',
      selectedArticle: null,
      articleToDelete: null,
      selectedCustomerName: '',
      selectedCustomerId: null,
      showStatus: {
        available: true,
        sold: true,
        returned: false,
        disposed: false,
      },
      labels: {
        available: 'Lagernd',
        sold: 'Verkauft',
        returned: 'Retourniert',
        disposed: 'Entsorgt',
      },
      sortKey: 'ArticleId',
      sortAsc: true,
      includePrice: true,
      onlyMarkedForReturn: false,       // Neuer Filter
    };
  },
  computed: {
    filteredArticles() {
      // 1. Status-Filter
      let filtered = this.articles.filter(
        (a) => this.showStatus[a.ArticleStatus]
      );

      // 2. Nur retour Markierte?
      if (this.onlyMarkedForReturn) {
        filtered = filtered.filter((a) => a.isMarkedForReturn);
      }

      // 3. Textsuche
      const terms = this.searchQuery
        .toLowerCase()
        .split(' ')
        .filter((t) => t);
      if (terms.length) {
        filtered = filtered.filter((article) => {
          const received = article.ReceivedDate
            ? new Date(article.ReceivedDate).toLocaleDateString('de-DE')
            : '';
          const sold = article.SaleDate
            ? new Date(article.SaleDate).toLocaleDateString('de-DE')
            : '';
          const articleId = article.ArticleId?.toString() || '';
          const comment = article.Comment?.toLowerCase() || '';
          const first = article.Customer?.FirstName?.toLowerCase() || '';
          const last = article.Customer?.LastName?.toLowerCase() || '';

          return terms.every(
            (term) =>
              articleId.includes(term) ||
              article.Name.toLowerCase().includes(term) ||
              article.Color?.toLowerCase().includes(term) ||
              article.Size?.toLowerCase().includes(term) ||
              article.Gender?.toLowerCase().includes(term) ||
              received.includes(term) ||
              sold.includes(term) ||
              comment.includes(term) ||
              first.includes(term) ||
              last.includes(term)
          );
        });
      }

      // 4. Sortierung
      return filtered.sort((a, b) => {
        const mod = this.sortAsc ? 1 : -1;
        let va = a[this.sortKey],
          vb = b[this.sortKey];
        if (va instanceof Date) va = va.getTime();
        if (vb instanceof Date) vb = vb.getTime();
        if (typeof va === 'string') return va.localeCompare(vb) * mod;
        return (va < vb ? -1 : va > vb ? 1 : 0) * mod;
      });
    },
  },
  methods: {
    goBack() {
      this.$router.push('/items');
    },
    btnClass(status) {
      return this.showStatus[status]
        ? 'btn btn-primary'
        : 'btn btn-outline-secondary';
    },
    toggleStatus(status) {
      this.showStatus[status] = !this.showStatus[status];
    },
    sortBy(key) {
      if (this.sortKey === key) this.sortAsc = !this.sortAsc;
      else {
        this.sortKey = key;
        this.sortAsc = true;
      }
    },
    async fetchArticles() {
      this.loading = true;
      try {
        const res = await axios.get('/articles');
        this.articles = res.data;
      } catch (e) {
        console.error('Fehler beim Abrufen der Artikel:', e);
      } finally {
        this.loading = false;
      }
    },
    openEditModal(article) {
      this.selectedArticle = { ...article };
    },
    closeModal() {
      this.selectedArticle = null;
    },
    openDeleteModal(article) {
      this.articleToDelete = article;
    },
    closeDeleteModal() {
      this.articleToDelete = null;
    },
    async saveArticle(article) {
      try {
        if (article.ArticleStatus === 'returned') {
          article.ReturnDate = new Date().toISOString();
        } else if (article.ArticleStatus === 'disposed') {
          article.DisposalDate = new Date().toISOString();
        } else if (article.ArticleStatus === 'available') {
          article.ReturnDate = null;
          article.DisposalDate = null;
        }

        await axios.put(`/articles/${article.ArticleId}`, article);
        this.closeModal();
        this.fetchArticles();
      } catch (e) {
        console.error('Fehler beim Speichern des Artikels:', e);
        alert('Fehler beim Speichern des Artikels. Bitte erneut versuchen.');
      }
    },
    async deleteArticle() {
      try {
        await axios.delete(`/articles/${this.articleToDelete.ArticleId}`);
        this.closeDeleteModal();
        this.fetchArticles();
      } catch (e) {
        console.error(e);
      }
    },
    generatePdf() {
      if (this.filteredArticles.length) {
        const first = this.filteredArticles[0];
        const customer = first.Customer;
        if (!customer) return;
        this.selectedCustomerName = `${customer.FirstName || ''} ${customer.LastName || ''}`.trim();
        this.selectedCustomerId = customer.id;
        this.$nextTick(() => this.$refs.pdfGenerator.generatePdf());
      }
    },
  },
  created() {
    this.fetchArticles();
  },
};
</script>

<style scoped>
.btn {
  min-width: 100px;
}

.table th,
.table td {
  vertical-align: middle;
  font-size: 0.9rem;
}
</style>
